import { CalendarDays, MapPin, Clock, HeartHandshake, ArrowUpRight, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { CONTACT } from "@/data";

const DETAILS = [
  { icon: CalendarDays, label: "Saturday, September 26, 2026" },
  { icon: Clock, label: "11:00 AM – 3:00 PM" },
  { icon: MapPin, label: "Stone Terrace · 6659 Chupp Rd, Stonecrest, GA 30038" },
];

export default function CommunityPage() {
  return (
    <div data-testid="community-page">
      <PageHeader
        overline="Community · Events & Announcements"
        title="Community event"
        accent="bulletin."
        subtitle="Heritage doesn't just protect families on paper — we show up. Here's where you'll find us in the community, and what's coming next."
      />

      {/* Featured event */}
      <section className="relative pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div
              data-testid="community-featured-event"
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 rounded-3xl border border-[#d4af37]/30 bg-[#0f2744]/60 p-8 md:p-12"
              style={{ boxShadow: "0 0 60px rgba(212,175,55,0.10)" }}
            >
              <div className="lg:col-span-5">
                <div className="rounded-2xl border-2 border-[#d4af37]/60 p-1.5 bg-[#081226] max-w-md mx-auto">
                  <img
                    src="/community/lets-talk-give-back-flyer.png"
                    alt="Let's Talk & Give Back — Clothing. Connections & Community. September 26, 2026 at Stone Terrace, Stonecrest GA"
                    data-testid="community-flyer"
                    className="rounded-xl w-full h-auto"
                  />
                </div>
              </div>
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 bg-[#d4af37]/10 px-4 py-1.5 font-body text-[11px] font-bold tracking-[0.25em] uppercase text-[#d4af37]">
                  <Megaphone size={13} /> Upcoming Event
                </span>
                <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white">
                  Let's Talk <span className="text-gold-gradient italic">&amp; Give Back.</span>
                </h2>
                <p className="mt-2 font-body text-sm tracking-[0.2em] uppercase text-white/50">
                  Clothing · Connections &amp; Community
                </p>
                <p className="mt-5 font-body font-light text-base md:text-lg leading-relaxed text-white/70">
                  Heritage Family Protection Agency is proud to stand alongside community partners who care at
                  <strong className="font-semibold text-white"> Let's Talk &amp; Give Back</strong> — a community
                  conversation and resource event featuring <strong className="font-semibold text-white">Tammy Grimes</strong>,
                  former Stonecrest councilwoman, community advocate, educator and mentor, and
                  <strong className="font-semibold text-white"> Wanda Wallace</strong> of When Purpose Meets Destiny.
                  Come for the clothing drive, stay for the connections — and stop by our table to talk about
                  protecting what matters most to your family.
                </p>
                <p className="mt-4 font-body font-light text-base md:text-lg leading-relaxed text-white/70">
                  <strong className="font-semibold text-[#d4af37]">We'll be there</strong> at Stone Terrace on
                  Saturday, September 26, 2026 from 11:00 AM to 3:00 PM — conversations, resources, and a stronger
                  tomorrow, together.
                </p>
                <ul className="mt-7 space-y-3">
                  {DETAILS.map((d) => (
                    <li key={d.label} className="flex items-center gap-3 font-body text-sm md:text-base text-white/80">
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[#d4af37]/35 text-[#d4af37]">
                        <d.icon size={16} strokeWidth={1.6} />
                      </span>
                      {d.label}
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-4">
                  <a
                    href="https://maps.google.com/?q=6659+Chupp+Rd,+Stonecrest,+GA+30038"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="community-directions-btn"
                    className="btn-sweep inline-flex items-center gap-2 rounded-full bg-gold-metallic px-7 py-3.5 font-body text-sm font-semibold tracking-wide text-black"
                  >
                    Get Directions <ArrowUpRight size={15} />
                  </a>
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="community-call-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-[#d4af37]/40 px-7 py-3.5 font-body text-sm tracking-wide text-white/80 hover:text-[#fcf6ba] hover:border-[#d4af37] transition-colors duration-300"
                  >
                    Questions? Call {CONTACT.phone}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Announcements */}
          <Reveal delay={0.08}>
            <div className="mt-16">
              <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Announcements</p>
              <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white">
                From the <span className="text-gold-gradient italic">Heritage board.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-2xl border border-white/10 bg-[#0f2744]/60 p-7 hover:border-[#d4af37]/50 transition-colors duration-500" data-testid="community-announcement-0">
                <div className="flex items-center gap-3">
                  <HeartHandshake size={20} className="text-[#d4af37]" strokeWidth={1.5} />
                  <p className="font-body text-[11px] tracking-[0.25em] uppercase text-white/45">September 26, 2026</p>
                </div>
                <h3 className="mt-4 font-serif-display text-2xl text-white">Find us at Let's Talk &amp; Give Back</h3>
                <p className="mt-3 font-body font-light text-sm md:text-base leading-relaxed text-white/60">
                  Our team will be on site at Stone Terrace in Stonecrest from 11:00 AM to 3:00 PM alongside The Ice
                  King Italian Ice, Healing Minds Through Design, and Yesss Cheesecakes. Come say hello — free
                  resources, real conversations, and community partners who care about you.
                </p>
                <Link to="/book-a-call" data-testid="community-announcement-book" className="mt-5 inline-flex items-center gap-2 font-body text-sm font-semibold text-[#d4af37] hover:text-[#fcf6ba] transition-colors">
                  Can't make it? Book a call instead <ArrowUpRight size={14} />
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <div className="h-full rounded-2xl border border-dashed border-white/15 bg-[#0a192f] p-7 flex flex-col items-start justify-center" data-testid="community-announcement-more">
                <Megaphone size={22} className="text-white/30" strokeWidth={1.5} />
                <h3 className="mt-4 font-serif-display text-2xl text-white/70">More events on the way</h3>
                <p className="mt-3 font-body font-light text-sm md:text-base leading-relaxed text-white/45">
                  New community events, giveaways, and announcements are posted here first. Check back soon — or follow
                  us on social media so you never miss where Heritage shows up next.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
